// MobileTabBar — нижняя навигация. Состав как HeaderNav:
// Discover / Trenches / Portfolio / Rewards.
// Новые вкусы (2026-08-17): simple / blob / dock / slide —
// разные конструкции, выбранная всегда тёмная, не белая.

import type { CSSProperties } from 'react'
import { Compass, Lightning, Trophy, Wallet } from '../ui/icons/app'
import styles from './MobileTabBar.module.css'

export type TabBarVariant =
  | 'pill'
  | 'sheet'
  | 'island'
  | 'glass'
  | 'pixelStair'
  | 'pixelDot'
  | 'aurora'
  | 'netflix'
  | 'netflixNew'
  | 'liquid'
  | 'simple'
  | 'blob'
  | 'dock'
  | 'slide'

export const GLASS_RECIPES: Record<
  string,
  { tint: number; blur: number; sat: number; bright: number; lens: number }
> = {
  netflix: { tint: 14, blur: 14, sat: 190, bright: 1.14, lens: 0 },
  netflixNew: { tint: 8, blur: 16, sat: 210, bright: 1.2, lens: 0 },
  liquid: { tint: 12, blur: 18, sat: 185, bright: 1.12, lens: 0 },
  aurora: { tint: 12, blur: 18, sat: 180, bright: 1.1, lens: 0 },
  simple: { tint: 44, blur: 18, sat: 150, bright: 1.04, lens: 0 },
  blob: { tint: 22, blur: 20, sat: 175, bright: 1.08, lens: 0 },
  dock: { tint: 40, blur: 20, sat: 155, bright: 1.05, lens: 0 },
  slide: { tint: 28, blur: 18, sat: 160, bright: 1.06, lens: 0 },
}

const GLASSY = new Set([
  'netflix',
  'netflixNew',
  'liquid',
  'aurora',
  'simple',
  'blob',
  'dock',
  'slide',
])

const ITEMS = [
  { label: 'Discover', to: '/discover', Icon: Compass },
  { label: 'Trenches', to: '/pulse', Icon: Lightning },
  { label: 'Portfolio', to: '/portfolio', Icon: Wallet },
  { label: 'Rewards', to: '/rewards', Icon: Trophy },
] as const

export function MobileTabBar({
  variant,
  active = 'Trenches',
  onNavigate,
  glassTint,
  glassBlur,
  lensTint,
  plateInset,
}: {
  variant: TabBarVariant
  active?: string
  onNavigate?: (to: string) => void
  glassTint?: number
  glassBlur?: number
  lensTint?: number
  /** Отступ пилюли выбранной вкладки, одинаковый со всех сторон. */
  plateInset?: number
}) {
  const recipe = GLASS_RECIPES[variant]
  const tint = glassTint ?? recipe?.tint ?? 22
  const blur = glassBlur ?? recipe?.blur ?? 24
  const lens = lensTint ?? recipe?.lens ?? 8
  const inset = plateInset ?? 3
  const sat = recipe?.sat ?? 180
  const bright = recipe?.bright ?? 1.05
  const icon = variant === 'blob' || variant === 'dock' ? 24 : 22
  const slideAt = Math.max(
    0,
    ITEMS.findIndex((it) => it.label === active)
  )

  const style = (
    GLASSY.has(variant)
      ? {
          backgroundColor: `rgb(16 16 18 / ${tint}%)`,
          backdropFilter: `blur(${blur}px) saturate(${sat}%) brightness(${bright})`,
          WebkitBackdropFilter: `blur(${blur}px) saturate(${sat}%) brightness(${bright})`,
          '--glass-tint': `${tint}%`,
          '--glass-blur': `${blur}px`,
          '--lens-tint': `${lens}%`,
          '--plate-inset': `${inset}px`,
          '--slide': String(slideAt),
        }
      : { '--slide': String(slideAt), '--plate-inset': `${inset}px` }
  ) as CSSProperties & { '--slide': string; '--plate-inset': string }

  return (
    <nav className={`${styles.bar} ${styles[variant]}`} aria-label="Sections" style={style}>
      {variant === 'slide' ? <span className={styles.slidePill} aria-hidden="true" /> : null}
      {ITEMS.map(({ label, to, Icon }) => {
        const on = label === active
        return (
          <button
            key={label}
            type="button"
            className={styles.tab}
            data-on={on}
            aria-current={on ? 'page' : undefined}
            onClick={() => onNavigate?.(to)}
          >
            <span className={styles.plate} aria-hidden="true" />
            <Icon size={icon} />
            <span className={styles.label}>{label}</span>
          </button>
        )
      })}
    </nav>
  )
}
